import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { faHome, faUpload, faTasksAlt, faAnalytics, faGlobePointer } from "@fortawesome/pro-light-svg-icons";
import { PiensoApp } from "./interfaces";

export interface LinkVO {
  name: string;
  app: PiensoApp;
  children: LinkVO[];
  active: boolean;
  role?: string;
  route?: string[];
  patterns?: string[];
  selected?: boolean;
  icon?: IconDefinition;
  hideFromMenu?: boolean;
}

export interface CategoryVO {
  display: LinkVO;
  children: LinkVO[];
}

export interface CategoryHash {
  [section: string]: CategoryVO;
}

export const PARENT_ROUTES = {
  HOME: {
    name: "Home",
    app: "CORE" as PiensoApp,
    route: ["home"],
    icon: faHome,
    children: [],
    active: true,
  },
  INGEST: {
    name: "Ingest",
    app: "INGEST" as PiensoApp,
    route: ["library"],
    icon: faUpload,
    children: [],
    active: true,
  },
  MODELS: {
    name: "Train",
    app: "CORE" as PiensoApp,
    route: ["lenses"],
    icon: faTasksAlt,
    children: [],
    active: false,
  },
  ANALYSIS: {
    name: "Analyze",
    app: "ANALYSIS" as PiensoApp,
    route: ["deploy"],
    icon: faAnalytics,
    children: [],
    active: false,
  },
  DEPLOY: {
    name: "Deploy",
    app: "ANALYSIS" as PiensoApp,
    route: ["deploy"],
    icon: faGlobePointer,
    children: [],
    active: true,
  },
};

export const CHILD_ROUTES = {
  HOME: {
    name: "Home",
    app: "CORE" as PiensoApp,
    role: "home:full",
    route: ["home"],
    hideFromMenu: true,
    children: [],
    active: true,
  },
  DATA_LIBRARY: {
    name: "Library",
    app: "INGEST" as PiensoApp,
    role: "ingest:full",
    route: ["library"],
    hideFromMenu: true,
    children: [],
    active: true,
  },
  TRAINING_DATA: {
    name: "Training Data",
    app: "CORE" as PiensoApp,
    role: "train:training_data:full",
    route: ["trainingData"],
    children: [],
    active: true,
  },
  STOPWORDS: {
    name: "Stop Words",
    app: "CORE" as PiensoApp,
    role: "train:fingerprinting:full",
    route: ["lenses", "stopwords"],
    children: [],
    active: true,
  },
  LIBRARY_MODELS: {
    name: "Models",
    app: "CORE" as PiensoApp,
    role: "train:fingerprinting:full",
    route: ["lenses"],
    children: [],
    active: true,
  },
  LIBRARY: {
    name: "Fingerprinting",
    app: "CORE" as PiensoApp,
    role: "train:fingerprinting:full",
    route: ["lenses"],
    patterns: ["lenses", "lenses/stopwords"],
    children: [],
    active: true,
  },
  DEEP_LEARNING: {
    name: "Deep Learning",
    app: "CORE" as PiensoApp,
    role: "train:deep_learning:full",
    route: ["dl"],
    children: [],
    active: true,
  },
  DEPLOY_MODELS: {
    name: "Deploy",
    app: "ANALYSIS" as PiensoApp,
    role: "deploy:full",
    route: ["deploy"],
    patterns: ["deploy(/*)"],
    hideFromMenu: true,
    children: [],
    active: true,
  },
  EXPLORE: {
    name: "Explore",
    app: "EXPLORE" as PiensoApp,
    role: "analyze:explore:full",
    route: [],
    patterns: ["browse/(*)", "project/:projectId(;*)"],
    children: [],
    active: true,
  },
  EXPLORATIONS: {
    name: "Explorations",
    app: "EXPLORE" as PiensoApp,
    role: "analyze:explore:full",
    route: ["browse", "explorations"],
    children: [],
    active: true,
  },
  DATA_SETS: {
    name: "Data Sets",
    app: "EXPLORE" as PiensoApp,
    role: "analyze:explore:full",
    route: ["browse", "data"],
    children: [],
    active: true,
  },
  VIEW_DASHBOARDS: {
    name: "Dashboards",
    app: "ANALYSIS" as PiensoApp,
    role: "analyze:dashboards:full",
    route: ["monitor"],
    patterns: ["monitor/dashboards(;*)", "monitor/:projectId(;*)"],
    children: [],
    active: true,
  },
  UNIT_LIST: {
    name: "Data Set Analysis",
    app: "CORE" as PiensoApp,
    role: "analyze:data_set_analysis:full",
    route: ["units"],
    patterns: ["units(/*)"],
    children: [],
    active: true,
  },
  TRAINING_WORKSPACE: {
    name: "Training",
    app: "CORE" as PiensoApp,
    role: "train:fingerprinting:full",
    route: ["lenses"],
    patterns: ["lenses/train(ing)/:lensId(*)"],
    hideFromMenu: true,
    children: [],
    active: true,
  },
  DEEP_LEARNING_WORKSPACE: {
    name: "Deep Learning",
    app: "CORE" as PiensoApp,
    role: "train:deep_learning:full",
    route: ["dl"],
    patterns: ["dl/:id(*)"],
    hideFromMenu: true,
    children: [],
    active: true,
  },
  MONITOR: {
    name: "Monitor",
    app: "MONITOR" as PiensoApp,
    role: "analyze:monitor:full",
    route: [],
    children: [],
    active: true,
  },
};

export const FULL_NAVIGATION: CategoryHash = {
  HOME: { display: PARENT_ROUTES.HOME, children: [] },
  INGEST: { display: PARENT_ROUTES.INGEST, children: [] },
  MODEL: { display: PARENT_ROUTES.MODELS, children: [] },
  ANALYSIS: { display: PARENT_ROUTES.ANALYSIS, children: [] },
  DEPLOY: { display: PARENT_ROUTES.DEPLOY, children: [] },
};
